import { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography';
import './cameraSourceDialogs.css';
import { LiveRegion } from '../atoms/LiveRegion';
import { Alert } from '../atoms/Alert';

export interface RevokeConfirmationDialogProps {
  /** Whether the dialog is currently open. */
  isOpen: boolean;
  /** Name of the camera source being revoked, shown so the owner knows which source is affected. */
  sourceName: string;
  /** Called when the user cancels (Escape, overlay click, close or Cancel button). */
  onCancel: () => void;
  /** Called ONLY via the explicit confirm button's onClick. */
  onConfirm: () => void;
  /** Whether the revoke request is currently in flight. */
  isSubmitting?: boolean;
}

export function RevokeConfirmationDialog({
  isOpen,
  sourceName,
  onCancel,
  onConfirm,
  isSubmitting = false,
}: RevokeConfirmationDialogProps) {
  const { t } = useTranslation();
  const announcementRef = useRef<HTMLElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Many screen readers only announce aria-live content *changes*, not the
  // initial mount content. The live region is therefore always mounted, and
  // its text is updated imperatively whenever the dialog opens or closes.
  useEffect(() => {
    if (!announcementRef.current) return;
    announcementRef.current.textContent = isOpen
      ? `${t('cameraSources.revokeConfirm.description')} ${t('cameraSources.revokeConfirm.warning')}`
      : '';
  }, [isOpen, t]);

  // Safer default for a destructive action: focus Cancel. This effect runs
  // after Dialog's own focus-on-open effect (child effects run first).
  useEffect(() => {
    if (isOpen) {
      cancelRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <>
      <LiveRegion as="span" ref={announcementRef} aria-atomic="true" visuallyHidden />
      <Dialog
        isOpen={isOpen}
        onClose={onCancel}
        title={t('cameraSources.revokeConfirm.title')}
        size="sm"
        data-testid="revoke-confirmation-dialog"
      >
        <Stack gap="4">
          <Text weight="semibold">{sourceName}</Text>
          <Text>{t('cameraSources.revokeConfirm.description')}</Text>
          <Alert tone="warning" live="off" compact>
            {t('cameraSources.revokeConfirm.warning')}
          </Alert>
          <Inline justify="end" gap="2" className="camera-source-dialog__actions">
            <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {t('cameraSources.revokeConfirm.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={onConfirm}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {t('cameraSources.revokeConfirm.confirm')}
            </Button>
          </Inline>
        </Stack>
      </Dialog>
    </>
  );
}
